import { cn } from '@/lib/utils';
import { ChevronDown } from 'lucide-react';
import { forwardRef, type SelectHTMLAttributes, type ReactNode } from 'react';

export type SelectSize = 'sm' | 'md';

interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
    size?: SelectSize;
    /** Class on the chevron-positioning wrapper (e.g. `flex w-full` for form fields). */
    wrapperClassName?: string;
    children?: ReactNode;
}

// sm is 24px and md is 28px. A coarse pointer raises each size to 32px.
const SIZE: Record<SelectSize, string> = {
    sm: 'h-6 pl-2 pr-6 text-annotation pointer-coarse:h-8',
    md: 'h-7 pl-2.5 pr-7 text-annotation pointer-coarse:h-8',
};

const CHEVRON_POS: Record<SelectSize, string> = {
    sm: 'right-1.5 h-3 w-3',
    md: 'right-2 h-3.5 w-3.5',
};

// Styled wrapper over the native <select>. Token borders/bg + a chevron glyph,
// matching the secondary Button's visual weight. Keeps native form semantics
// (value/onChange/disabled/aria all pass straight through). Replaces the bare
// `<select className="...">` instances dotted across the pages.
//
// Side-by-side uniformity rule (see Button.tsx): `Select` shares one height per
// size with `Button` (`md` = 28px, `sm` = 24px) and the same radius/border, so a
// dropdown lines up exactly with the buttons beside it. Give a `Select` the
// SAME size as the buttons in its row — never a `sm` dropdown next to `md`
// buttons.
const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
    { size = 'md', className, wrapperClassName, children, ...rest },
    ref,
) {
    return (
        <div className={cn('relative inline-flex', wrapperClassName)}>
            <select
                ref={ref}
                className={cn(
                    'w-full appearance-none rounded-button font-medium',
                    'bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text-primary))]',
                    'border border-[rgb(var(--color-divider))]',
                    'transition-colors duration-card-hover cursor-pointer',
                    'hover:border-[rgb(var(--color-text-primary)/0.2)]',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--color-accent)/0.4)]',
                    'disabled:opacity-50 disabled:cursor-not-allowed',
                    SIZE[size],
                    className,
                )}
                {...rest}
            >
                {children}
            </select>
            <ChevronDown
                aria-hidden="true"
                className={cn(
                    'pointer-events-none absolute top-1/2 -translate-y-1/2 text-[rgb(var(--color-text-tertiary))]',
                    CHEVRON_POS[size],
                )}
            />
        </div>
    );
});

export default Select;
